import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Segmented,
  SkeletonLoader,
} from '@insight-health-ai/roseate/components';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useGetAppointmentList } from 'src/hooks/appointment';
import styled from '@emotion/styled';
import { ChevronLeft, ChevronRight } from '@insight-health-ai/roseate/icons';
import dayjs from 'dayjs';
import { getAppointmentStatusColor } from 'src/utilities/appointments';
import { Appointment } from 'src/models';
import { NotificationHistoryDrawer } from '../details/notificationHistory';
import { AppointmentSort, AppointmentFilters } from './helpers';
import { UseAppointmentActionsProps } from './actions';

type AppointmentListCalendarViewProps = {
  filters: AppointmentFilters;
  sort: AppointmentSort;
} & Pick<UseAppointmentActionsProps, 'onEditAppointment'>;

const StyledCalendar = styled(FullCalendar)``;

export const AppointmentListCalendarView = ({
  filters,
  onEditAppointment,
}: AppointmentListCalendarViewProps) => {
  const [_, setCalendarUpdateCounter] = useState(0);
  const [detailsAppointment, setDetailsAppointment] =
    useState<Appointment | null>(null);
  const [viewType, setViewType] = useState<
    'timeGridDay' | 'timeGridWeek' | 'dayGridMonth'
  >('timeGridDay');

  const calendarRef = useRef<FullCalendar | null>(null);
  const calendarApi = useMemo(() => {
    return calendarRef.current?.getApi();
  }, [calendarRef.current]);

  const { startDate: _startDate, endDate: _endDate, ...rest } = filters;
  const { data, isLoading, isFetching } = useGetAppointmentList(
    {
      startDate: calendarApi?.view.activeStart
        ? dayjs(calendarApi?.view.activeStart).format('YYYY-MM-DD')
        : undefined,
      endDate: calendarApi?.view.activeEnd
        ? dayjs(calendarApi?.view.activeEnd).format('YYYY-MM-DD')
        : undefined,
      ...rest,
    },
    {
      enabled: !!calendarApi,
    }
  );

  // This is a hack to force the calendar to update when the view type changes
  // This is because the calendar does not update when the view type changes
  // and the events are not re-rendered
  const updateCalendar = useCallback(() => {
    setCalendarUpdateCounter((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (calendarApi) {
      calendarApi.changeView(viewType);
      updateCalendar();
    }
  }, [viewType]);

  const events = useMemo(() => {
    return data?.appointments?.map((item) => {
      const title = `${item.patient_firstName} ${item.patient_lastName}`;

      return {
        id: item.appointment_id.toString(),
        title,
        start: new Date(item.start_date_local),
        classNames: 'p-1',
        backgroundColor: getAppointmentStatusColor(item.chat_completion_status)
          .bgColor,
        textColor: getAppointmentStatusColor(item.chat_completion_status)
          .textColor,
        extendedProps: {
          ...item,
        },
      };
    });
  }, [data]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center px-2 py-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Button
            variant="outlined"
            appearance="primary"
            icon={<ChevronLeft />}
            onClick={() => {
              calendarApi?.prev();
              updateCalendar();
            }}
          />
          <Button
            variant="outlined"
            appearance="primary"
            onClick={() => {
              calendarApi?.today();
              updateCalendar();
            }}
          >
            Today
          </Button>
          <Button
            variant="outlined"
            appearance="primary"
            icon={<ChevronRight />}
            onClick={() => {
              calendarApi?.next();
              updateCalendar();
            }}
          />
        </div>
        <div className="flex flex-col justify-center text-center">
          <div className="font-medium">{calendarApi?.view?.title}</div>
          {isFetching ? (
            <SkeletonLoader
              active
              paragraph={{ rows: 1, width: 200 }}
              title={false}
            />
          ) : (
            !!data?.appointments && (
              <div className="text-xs text-gray-500">
                Found {data?.appointments?.length} appointments
              </div>
            )
          )}
        </div>
        <Segmented
          options={[
            { label: 'Day', value: 'timeGridDay' },
            { label: 'Week', value: 'timeGridWeek' },
            { label: 'Month', value: 'dayGridMonth' },
          ]}
          value={viewType}
          onChange={(value) =>
            setViewType(
              value as 'timeGridDay' | 'timeGridWeek' | 'dayGridMonth'
            )
          }
        />
      </div>
      <StyledCalendar
        loading={() => {
          return isLoading;
        }}
        ref={(ref) => {
          calendarRef.current = ref;
          setTimeout(() => {
            updateCalendar();
          }, 500);
        }}
        events={events}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        height="100%"
        headerToolbar={
          false && {
            left: filters?.practitionerIds?.length
              ? 'prev today next customPractitionerFilter'
              : 'prev today next',
            center: 'title',
            right: 'timeGridDay,timeGridWeek,dayGridMonth',
          }
        }
        dayMaxEvents={9}
        eventClick={({ event }) => {
          setDetailsAppointment(event._def.extendedProps as Appointment);
        }}
        initialView={viewType}
      />
      {detailsAppointment && (
        <NotificationHistoryDrawer
          title="Appointment details"
          appointmentUuid={detailsAppointment?.appt_uuid}
          open={!!detailsAppointment}
          onClose={() => setDetailsAppointment(null)}
          onEditAppointment={onEditAppointment}
        />
      )}
    </div>
  );
};
